import { type PrincipalAndRole, type GeneralAccessLevel } from '../controlled/commonTypes.tsx';

import type { PrincipalsMap } from './principalsMap.ts';
import type { SpecificAccessDelta } from './masterState.ts';

export function makeSpecificAccessList(
	{ removeAll }: SpecificAccessDelta,
	stagedGeneralAccessLevel: GeneralAccessLevel,
	principalsMap: PrincipalsMap,
	currentUserId: string,
	queryPrincipals: PrincipalAndRole[],
	principalsPendingAccess: PrincipalAndRole[],
): {
	ids: string[];
	hasPrincipalsBesidesCurrentUser: boolean;
} {
	const ids = new Set<string>();
	let hasPrincipalsBesidesCurrentUser = false;

	/**
	 * Calculates if a principal's role would not grant them access beyond what
	 * the General Access level would automatically afford them.
	 */
	function isCoveredByGeneralAccess(id: string): boolean {
		const theirRole = principalsMap.get(id)?.role;
		if (theirRole === 'can-edit') {
			if (stagedGeneralAccessLevel === 'open-can-edit') return true;
		} else if (theirRole === 'can-view') {
			if (stagedGeneralAccessLevel === 'open-can-edit') return true;
			if (stagedGeneralAccessLevel === 'open-can-view') return true;
		}
		return false;
	}

	// List (You) first, if the current user is even on there as an individual
	if (principalsMap.has(currentUserId)) {
		if (!isCoveredByGeneralAccess(currentUserId)) {
			ids.add(currentUserId);
		}
	}

	// Next, all the principals from the query, if 'Remove all' was never pressed,
	// omitting the ones that have already been pushed
	if (!removeAll) {
		for (const {
			principal: { id },
		} of [...queryPrincipals, ...principalsPendingAccess]) {
			// Omit (You)
			if (id === currentUserId) continue;

			// Omit removals
			if (!principalsMap.has(id)) continue;

			// Omit roles that don't go beyond current General Access allowances
			if (isCoveredByGeneralAccess(id)) continue;

			ids.add(id);
			hasPrincipalsBesidesCurrentUser = true;
		}
	}

	return { ids: Array.from(ids), hasPrincipalsBesidesCurrentUser };
}
